import React, { createContext, useContext, useState, useEffect } from 'react';

// Define ResponsiveVoice-compatible voice options
interface VoiceOption {
  name: string;
  label: string;
}

const voiceOptions: VoiceOption[] = [
  { name: 'US English Female', label: 'US Female' },
  { name: 'US English Male', label: 'US Male' },
  { name: 'UK English Female', label: 'UK Female' },
  { name: 'UK English Male', label: 'UK Male' },
  { name: 'Australian Female', label: 'Australian Female' },
];

interface SettingsContextType {
  fontSize: number;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  isDarkMode: boolean;
  isAutoMode: boolean;
  setIsAutoMode: (value: boolean) => void;
  toggleDarkMode: () => void;
  selectedVoice: VoiceOption | null;
  setSelectedVoice: (voice: VoiceOption | null) => void;
  speechRate: number;
  setSpeechRate: (rate: number) => void;
  voiceOptions: VoiceOption[];
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 24;
const FONT_SIZE_STEP = 2;

// Helper function to determine if it's daytime (6 AM to 6 PM)
const isDaytime = (): boolean => {
  const now = new Date();
  const hours = now.getHours();
  return hours >= 6 && hours < 18; // Daytime: 6 AM to 6 PM
};

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('fontSize');
    return saved ? parseInt(saved, 10) : 16;
  });

  const [isAutoMode, setIsAutoMode] = useState(() => {
    const saved = localStorage.getItem('isAutoMode');
    return saved ? saved === 'true' : true;
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) return savedDarkMode === 'true';
    return !isDaytime(); // Default to time-based theme
  });

  const [selectedVoice, setSelectedVoice] = useState<VoiceOption | null>(null);
  const [speechRate, setSpeechRate] = useState(() => {
    const saved = localStorage.getItem('speechRate');
    return saved ? parseFloat(saved) : 0.8;
  });

  useEffect(() => {
    localStorage.setItem('fontSize', fontSize.toString());
    document.documentElement.style.fontSize = `${fontSize}px`;
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('isAutoMode', isAutoMode.toString());
    if (!isAutoMode) {
      localStorage.setItem('darkMode', isDarkMode.toString());
    } else {
      localStorage.removeItem('darkMode');
    }
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode, isAutoMode]);

  // Update theme based on time of day when in auto mode
  useEffect(() => {
    if (!isAutoMode) return;

    const updateTheme = () => {
      setIsDarkMode(!isDaytime());
    };

    // Initial update
    updateTheme();

    // Update every minute to handle time changes (e.g., crossing 6 AM/PM)
    const interval = setInterval(updateTheme, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAutoMode]);

  useEffect(() => {
    localStorage.setItem('speechRate', speechRate.toString());
  }, [speechRate]);

  useEffect(() => {
    const initVoices = () => {
      const savedVoiceName = localStorage.getItem('selectedVoice');
      if (savedVoiceName) {
        const voice = voiceOptions.find(v => v.name === savedVoiceName);
        setSelectedVoice(voice || voiceOptions[0]);
      } else {
        setSelectedVoice(voiceOptions[0]);
      }
    };
    initVoices();
  }, []);

  useEffect(() => {
    if (selectedVoice) {
      localStorage.setItem('selectedVoice', selectedVoice.name);
    }
  }, [selectedVoice]);

  const increaseFontSize = () => {
    setFontSize(prev => Math.min(prev + FONT_SIZE_STEP, MAX_FONT_SIZE));
  };

  const decreaseFontSize = () => {
    setFontSize(prev => Math.max(prev - FONT_SIZE_STEP, MIN_FONT_SIZE));
  };

  const toggleDarkMode = () => {
    setIsAutoMode(false);
    setIsDarkMode(prev => !prev);
  };

  return (
    <SettingsContext.Provider
      value={{
        fontSize,
        increaseFontSize,
        decreaseFontSize,
        isDarkMode,
        isAutoMode,
        setIsAutoMode,
        toggleDarkMode,
        selectedVoice,
        setSelectedVoice,
        speechRate,
        setSpeechRate,
        voiceOptions,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}